import React, { useMemo } from 'react';
import { ChevronRight, FileSpreadsheet } from 'lucide-react';
import fileAssetsThumbnail1 from 'src/assets/images/sites/file-assets-thumbnail-1.png';
import fileAssetsThumbnail2 from 'src/assets/images/sites/file-assets-thumbnail-2.png';
import fileAssetsThumbnail3 from 'src/assets/images/sites/file-assets-thumbnail-3.png';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { useSiteFilesQuery } from '../../site-files/state/api/siteFilesGraphSlice';

type SiteFileAssetsProps = {
    projectId: string;
};

const SiteFileAssets = ({ projectId }: SiteFileAssetsProps) => {
    const { currentData: siteFiles = [], isFetching: isSiteFilesFetching } = useSiteFilesQuery({ projectId: projectId || '' });

    const { toSiteFiles } = useAppNavigation();

    const { pdfCount, csvXlsxCount, imageCount } = useMemo(
        () =>
            siteFiles.reduce(
                (acc, f) => {
                    const ext = (f.name.split('.').pop() || '').toLowerCase();
                    if (ext === 'pdf') acc.pdfCount++;
                    else if (ext === 'csv' || ext === 'xlsx') acc.csvXlsxCount++;
                    else if (ext === 'png' || ext === 'jpg' || ext === 'jpeg') acc.imageCount++;
                    return acc;
                },
                { pdfCount: 0, csvXlsxCount: 0, imageCount: 0 }
            ),
        [siteFiles]
    );

    if (isSiteFilesFetching) {
        return null;
    }

    return (
        <div className={`flex-1 flex self-stretch flex-col rounded-lg shadow-portfolio-summary-card p-4 bg-white`}>
            <div className='flex flex-col lg:flex-row items-start gap-4 justify-start'>
                <button type='button' onClick={() => toSiteFiles()} className='flex gap-2 items-center text-left w-full lg:w-auto'>
                    <FileSpreadsheet className='text-primary' />
                    <div className='text-[17px] text-primary'>Files</div>
                    <ChevronRight className='h-4 w-4 text-primary' />
                </button>

                <FileAssetCard
                    imageSrc={fileAssetsThumbnail1}
                    title='PDFs & Reports'
                    count={pdfCount}
                    description='Access PDFs and any Environmental DNA reports.'
                    onClick={() => toSiteFiles({ filterFileType: 'pdf' })}
                />

                <FileAssetCard
                    imageSrc={fileAssetsThumbnail2}
                    title='CSV & OTU files'
                    count={csvXlsxCount}
                    description='Download CSVs and available eDNA sample data.'
                    onClick={() => toSiteFiles({ filterFileType: 'csv' })}
                />

                <FileAssetCard
                    imageSrc={fileAssetsThumbnail3}
                    title='Images'
                    count={imageCount}
                    description='Access images and additional eDNA data-visualisations.'
                    onClick={() => toSiteFiles({ filterFileType: 'images' })}
                />
            </div>
        </div>
    );
};

const FileAssetCard = ({
    imageSrc,
    title,
    count,
    description,
    onClick,
}: {
    imageSrc: string;
    title: string;
    count: number;
    description: string;
    onClick: () => void;
}) => (
    <button
        disabled={count === 0}
        onClick={onClick}
        className={`flex gap-2 items-start text-left w-full lg:w-auto ${
            count === 0 ? 'cursor-default' : 'cursor-pointer hover:opacity-80'
        }`}
    >
        <img src={imageSrc} alt={`${title} image`} className='w-[100px] h-[56px] border-2 border-grey-40 rounded-lg' />
        <div className='flex flex-col gap-1'>
            <div className='text-base flex gap-2 items-center'>
                {title}
                <span className='rounded-full bg-grey-20 min-w-[1.5rem] h-6 flex items-center justify-center text-sm leading-none px-1'>
                    {count}
                </span>
                {count > 0 && <ChevronRight className='h-4 w-4' />}
            </div>
            <div className='text-xs text-grey-80'>{description}</div>
        </div>
    </button>
);

export default SiteFileAssets;
